import React, { memo, useCallback, useRef } from "react";
import { Box } from "@chakra-ui/react";
import { boundsOf, MIN_SIZE } from "../boardGeometry";
import { STANDE } from "../../standeTokens";

const HANDLE = 9;

/**
 * Where each handle sits, and what dragging it moves.
 *
 * `x`/`y` is which edge the handle drags: -1 the left or top, 1 the right or
 * bottom, 0 neither. A corner moves two edges, an edge handle one.
 */
const HANDLES = [
  { id: "nw", x: -1, y: -1, cursor: "nwse-resize" },
  { id: "n", x: 0, y: -1, cursor: "ns-resize" },
  { id: "ne", x: 1, y: -1, cursor: "nesw-resize" },
  { id: "e", x: 1, y: 0, cursor: "ew-resize" },
  { id: "se", x: 1, y: 1, cursor: "nwse-resize" },
  { id: "s", x: 0, y: 1, cursor: "ns-resize" },
  { id: "sw", x: -1, y: 1, cursor: "nesw-resize" },
  { id: "w", x: -1, y: 0, cursor: "ew-resize" },
];

/** Text only has a width worth setting; its height follows the words. */
const HANDLES_FOR = {
  text: HANDLES.filter((h) => h.y === 0),
};

/**
 * The frame around the selected item, with handles to resize it.
 *
 * Dragged the way FigJam and Miro do it: the opposite edge stays put, Shift
 * keeps the proportions from a corner, and an item cannot be dragged smaller
 * than it can be read. The whole drag is one change to undo — the board is
 * marked when it starts and each frame after that is not recorded.
 *
 * Pointer deltas are divided by the zoom, or a handle would run away from
 * the pointer the moment the board was scaled.
 */
const SelectionFrame = ({ item, scale = 1, onResize, onResizeStart }) => {
  const b = boundsOf(item);
  const origin = useRef(null);

  const start = useCallback(
    (handle) => (e) => {
      e.stopPropagation();
      e.preventDefault();
      onResizeStart?.();
      origin.current = { px: e.clientX, py: e.clientY, box: boundsOf(item) };
      const min = MIN_SIZE[item.kind] || 12;
      const ratio = origin.current.box.width / origin.current.box.height || 1;

      const onMove = (ev) => {
        const o = origin.current;
        if (!o) return;
        const dx = (ev.clientX - o.px) / scale;
        const dy = (ev.clientY - o.py) / scale;

        let { x, y, width, height } = o.box;
        if (handle.x === 1) width = o.box.width + dx;
        if (handle.x === -1) width = o.box.width - dx;
        if (handle.y === 1) height = o.box.height + dy;
        if (handle.y === -1) height = o.box.height - dy;

        // Shift on a corner holds the shape of the thing, like everywhere.
        if (ev.shiftKey && handle.x !== 0 && handle.y !== 0) {
          if (Math.abs(width / o.box.width) > Math.abs(height / o.box.height)) {
            height = width / ratio;
          } else {
            width = height * ratio;
          }
        }

        width = Math.max(width, min);
        height = Math.max(height, min);
        // The edge being dragged moves; the opposite one is where it was.
        if (handle.x === -1) x = o.box.x + (o.box.width - width);
        if (handle.y === -1) y = o.box.y + (o.box.height - height);

        const patch = { width: Math.round(width), x: Math.round(x) };
        if (item.kind !== "text") {
          patch.height = Math.round(height);
          patch.y = Math.round(y);
        }
        onResize(patch);
      };

      const onUp = () => {
        origin.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [item, scale, onResize, onResizeStart]
  );

  const handles = HANDLES_FOR[item.kind] || HANDLES;

  return (
    <Box
      position="absolute"
      left={`${b.x}px`}
      top={`${b.y}px`}
      width={`${b.width}px`}
      height={`${b.height}px`}
      border="1.5px solid"
      borderColor={STANDE.primaryBlue}
      borderRadius="2px"
      pointerEvents="none"
      zIndex={4}
      aria-hidden
    >
      {handles.map((h) => (
        <Box
          key={h.id}
          data-resize-handle={h.id}
          position="absolute"
          boxSize={`${HANDLE}px`}
          left={`calc(${(h.x + 1) * 50}% - ${HANDLE / 2}px)`}
          top={`calc(${(h.y + 1) * 50}% - ${HANDLE / 2}px)`}
          bg="#ffffff"
          border="1.5px solid"
          borderColor={STANDE.primaryBlue}
          borderRadius="2px"
          cursor={h.cursor}
          pointerEvents="auto"
          onPointerDown={start(h)}
          // Big enough to grab with a finger, without drawing a bigger box.
          _before={{ content: '""', position: "absolute", inset: "-6px" }}
        />
      ))}
    </Box>
  );
};

export default memo(SelectionFrame);
